// Client-side session builder. Runs fully offline from the bundled drill
// library so a kid can generate a plan at a field with no signal.
//
// Approach: fixed per-block time budgets scaled to the chosen duration; filter
// drills by the current constraints (equipment / helper / space / position);
// then weighted-random selection per block scored by focus match, difficulty
// fit, and how long since the drill was last done. Anchor drills are kept
// stable in the challenge block so benchmark progress stays comparable.

import {
  BLOCK_ORDER,
  type Block,
  type Constraints,
  type Drill,
  type DrillLibrary,
  type PlannedDrill,
  type Profile,
  type SessionPlan,
  type Space,
} from "../data/schema";

// ---- seeded RNG (deterministic for tests) --------------------------------
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- block budgets --------------------------------------------------------
const BUDGETS: Record<number, Record<Block, number>> = {
  30: { warmup: 5, technical: 7, position: 11, challenge: 4, cooldown: 3 },
  45: { warmup: 6, technical: 11, position: 16, challenge: 6, cooldown: 4 },
  60: { warmup: 8, technical: 15, position: 20, challenge: 7, cooldown: 5 },
};

export function blockBudget(durationMin: number): Record<Block, number> {
  return BUDGETS[durationMin] ?? BUDGETS[60];
}

const SPACE_RANK: Record<Space, number> = { small: 1, half: 2, full: 3 };

// Difficulty we aim for, by profile. Older keeper trains a touch harder.
function targetDifficulty(profile: Profile): number {
  const base = profile.position === "goalkeeper" ? 3.3 : 2.8;
  return base + Math.max(0, Math.min(1, (profile.age - 12) / 4)) * 0.7;
}

// ---- filtering ------------------------------------------------------------
export function filterDrills(
  drills: Drill[],
  profile: Profile,
  c: Constraints
): Drill[] {
  const haveSpace = SPACE_RANK[c.space];
  const equip = new Set(c.equipment.map((e) => e.toLowerCase()));
  return drills.filter((d) => {
    if (!d.position.includes(profile.position) && !d.position.includes("all"))
      return false;
    if (SPACE_RANK[d.space] > haveSpace) return false;
    // Solo means nobody to serve/partner.
    if (c.helper === "solo" && d.setupNeed !== "solo") return false;
    // Every listed piece of equipment must be on hand.
    for (const item of d.equipment) {
      if (!equip.has(item.toLowerCase())) return false;
    }
    return true;
  });
}

// ---- scoring --------------------------------------------------------------
export interface DrillContext {
  lastDoneDaysAgo: Map<string, number>; // drillId -> days since last completed
}

export function scoreDrill(
  d: Drill,
  profile: Profile,
  ctx: DrillContext,
  rng: () => number
): number {
  const focus = new Set(profile.focusAreas);
  const focusHits = d.skills.filter((s) => focus.has(s)).length;
  const focusScore = focusHits > 0 ? 1 + focusHits * 0.6 : 0.4;

  const diffGap = Math.abs(d.difficulty - targetDifficulty(profile));
  const difficultyScore = Math.max(0.2, 1 - diffGap * 0.28);

  // Longer since last done = fresher = higher. Never done = max freshness.
  const days = ctx.lastDoneDaysAgo.get(d.id);
  const recencyScore =
    days === undefined ? 1.2 : Math.min(1.2, 0.2 + days * 0.14);

  const jitter = 0.85 + rng() * 0.3; // variety
  return (focusScore * 1.5 + difficultyScore + recencyScore) * jitter;
}

function weightedPick(
  pool: { drill: Drill; weight: number }[],
  rng: () => number
): Drill | null {
  if (pool.length === 0) return null;
  const total = pool.reduce((s, p) => s + Math.max(0.0001, p.weight), 0);
  let r = rng() * total;
  for (const p of pool) {
    r -= Math.max(0.0001, p.weight);
    if (r <= 0) return p.drill;
  }
  return pool[pool.length - 1].drill;
}

// ---- generation -----------------------------------------------------------
export interface GenerateInput {
  profile: Profile;
  constraints: Constraints;
  library: DrillLibrary;
  history?: { drillId: string; date: string }[];
  now?: Date;
  seed?: number;
}

const MIN_BLOCK_DRILL_MIN = 4;

export function generateSession(input: GenerateInput): SessionPlan {
  const now = input.now ?? new Date();
  const rng = mulberry32(input.seed ?? (now.getTime() & 0xffffffff) >>> 0);
  const budgets = blockBudget(input.constraints.durationMin);

  // recency map
  const lastDoneDaysAgo = new Map<string, number>();
  for (const h of input.history ?? []) {
    const d = Math.max(
      0,
      Math.floor((now.getTime() - new Date(h.date).getTime()) / 86400000)
    );
    const prev = lastDoneDaysAgo.get(h.drillId);
    if (prev === undefined || d < prev) lastDoneDaysAgo.set(h.drillId, d);
  }
  const ctx: DrillContext = { lastDoneDaysAgo };

  const eligible = filterDrills(
    input.library.drills,
    input.profile,
    input.constraints
  );
  const used = new Set<string>();
  const planned: PlannedDrill[] = [];

  for (const block of BLOCK_ORDER) {
    let remaining = budgets[block];
    // Anchor first in the challenge block so the benchmark recurs (kept stable
    // for comparable progress), but only for THIS profile's position.
    if (block === "challenge") {
      const anchor = eligible.find(
        (d) =>
          d.block === "challenge" &&
          d.anchor &&
          !used.has(d.id) &&
          d.position.includes(input.profile.position)
      );
      if (anchor && rng() < 0.75) {
        const alloc = Math.min(anchor.minutes, Math.max(remaining, anchor.minutes));
        planned.push({ drill: anchor, block, allocatedMin: alloc });
        used.add(anchor.id);
        remaining -= alloc;
      }
    }

    let guard = 0;
    while (remaining >= MIN_BLOCK_DRILL_MIN && guard++ < 20) {
      const pool = eligible
        .filter(
          (d) =>
            d.block === block &&
            !used.has(d.id) &&
            d.minutes <= remaining + 3 // small slack
        )
        .map((d) => ({ drill: d, weight: scoreDrill(d, input.profile, ctx, rng) }));
      const pick = weightedPick(pool, rng);
      if (!pick) break;
      const alloc = Math.min(pick.minutes, remaining + 3);
      planned.push({ drill: pick, block, allocatedMin: Math.round(alloc) });
      used.add(pick.id);
      remaining -= alloc;
    }
  }

  return {
    id: cryptoRandomId(),
    profileId: input.profile.id,
    date: toDateStr(now),
    durationMin: input.constraints.durationMin,
    constraints: input.constraints,
    drills: planned,
    libraryVersion: input.library.version,
  };
}

export function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function cryptoRandomId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
